import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../services/http.js';
import { useToast } from '../hooks/useToast.jsx';
import { useConfirm } from '../hooks/useConfirm.jsx';
import { ErrorRetryCard } from '../components/common/ErrorRetryCard.jsx';

export default function TrilhasPage() {
  const [trails, setTrails] = useState(null);
  const [trailsError, setTrailsError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const { showApiErrorToast } = useToast();
  const confirm = useConfirm();

  const fetchTrails = async () => {
    setTrailsError(null);
    try {
      const { res, data } = await apiClient.request('/trails');
      if (res.ok) {
        setTrails(data);
        return;
      }
      setTrailsError(apiClient.parseApiError(data, 'Não foi possível carregar as trilhas.'));
    } catch {
      setTrailsError('Falha de conexão ao carregar as trilhas.');
    }
  };

  useEffect(() => { setTimeout(fetchTrails, 0); }, []);

  const handleSubscribe = async (trailId) => {
    setBusyId(trailId);
    try {
      const { res, data } = await apiClient.request(`/trails/${trailId}/subscribe`, { method: 'POST' });
      if (res.ok) {
        await fetchTrails();
      } else {
        showApiErrorToast(res, data, 'Não foi possível se inscrever nesta trilha.', { onRetry: () => handleSubscribe(trailId) });
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível se inscrever nesta trilha.', { onRetry: () => handleSubscribe(trailId) });
    }
    setBusyId(null);
  };

  const handleUnsubscribe = async (trailId) => {
    if (!(await confirm({
      title: 'Cancelar Inscrição',
      message: 'Cancelar sua inscrição nesta trilha? Seu progresso será reiniciado se você se inscrever novamente.',
      variant: 'danger',
      confirmText: 'Cancelar Inscrição'
    }))) return;
    setBusyId(trailId);
    try {
      const { res, data } = await apiClient.request(`/trails/${trailId}/subscribe`, { method: 'DELETE' });
      if (res.ok) {
        await fetchTrails();
      } else {
        showApiErrorToast(res, data, 'Não foi possível cancelar a inscrição.', { onRetry: () => handleUnsubscribe(trailId) });
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível cancelar a inscrição.', { onRetry: () => handleUnsubscribe(trailId) });
    }
    setBusyId(null);
  };

  const handlePause = async (trailId) => {
    setBusyId(trailId);
    try {
      const { res, data } = await apiClient.request(`/trails/${trailId}/pause`, { method: 'POST' });
      if (res.ok) {
        await fetchTrails();
      } else {
        showApiErrorToast(res, data, 'Não foi possível pausar a trilha.', { onRetry: () => handlePause(trailId) });
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível pausar a trilha.', { onRetry: () => handlePause(trailId) });
    }
    setBusyId(null);
  };

  const handleResume = async (trailId) => {
    setBusyId(trailId);
    try {
      const { res, data } = await apiClient.request(`/trails/${trailId}/resume`, { method: 'POST' });
      if (res.ok) {
        await fetchTrails();
      } else {
        showApiErrorToast(res, data, 'Não foi possível retomar a trilha.', { onRetry: () => handleResume(trailId) });
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível retomar a trilha.', { onRetry: () => handleResume(trailId) });
    }
    setBusyId(null);
  };

  return (
    <div className="min-h-screen bg-surface text-on-surface p-6">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <Link to="/chat" className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-surface-container-low hover:bg-surface-container-high rounded-lg font-medium transition-colors border border-outline-variant">
            <span>←</span> Voltar para o Chat
          </Link>
          <h1 className="text-2xl font-bold">Trilhas de Estudo</h1>
        </div>

        <p className="text-on-surface-variant text-sm">
          Inscreva-se em uma trilha e receba uma meditação diária por e-mail até completar o percurso.
        </p>

        <div className="space-y-4">
          {trails === null ? (
            trailsError ? (
              <ErrorRetryCard message={trailsError} onRetry={fetchTrails} />
            ) : (
              <p className="text-sm text-on-surface-variant">Carregando...</p>
            )
          ) : (
            trails.map((trail) => {
              const sub = trail.subscription;
              const isActive = sub?.active;
              const isPaused = isActive && sub.paused;
              const isCompleted = sub && !sub.active && sub.completed_at;
              const progressPct = sub ? Math.min(100, Math.round(((sub.current_day - 1) / trail.day_count) * 100)) : 0;

              return (
                <div key={trail.trail_id} className="bg-surface-container-low border border-outline-variant rounded-lg p-5 space-y-3">
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <Link to={`/trilhas/${trail.trail_id}`} className="font-bold text-lg hover:text-primary transition-colors">
                        {trail.title}
                      </Link>
                      <p className="text-sm text-on-surface-variant">{trail.description}</p>
                      <p className="text-xs text-on-surface-variant/70 mt-1">{trail.day_count} dias · 1 e-mail por dia</p>
                    </div>
                    {isCompleted && (
                      <span className="bg-primary-container text-on-primary-container px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
                        Concluída
                      </span>
                    )}
                    {isPaused && (
                      <span className="bg-surface-container-high text-on-surface-variant px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
                        Pausada
                      </span>
                    )}
                    {isActive && !isPaused && (
                      <span className="bg-surface-container-high text-on-surface px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
                        Em andamento
                      </span>
                    )}
                  </div>

                  {isActive && (
                    <div>
                      <div className="flex justify-between text-xs text-on-surface-variant mb-1">
                        <span>Dia {sub.current_day} de {trail.day_count}</span>
                        <span>{progressPct}%</span>
                      </div>
                      <div className="bg-surface-container-highest rounded-full h-2 overflow-hidden">
                        <div className="bg-primary h-full" style={{ width: `${progressPct}%` }}></div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    {isActive ? (
                      <>
                        {isPaused ? (
                          <button
                            onClick={() => handleResume(trail.trail_id)}
                            disabled={busyId === trail.trail_id}
                            className="px-4 py-2 bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50"
                          >
                            Retomar
                          </button>
                        ) : (
                          <button
                            onClick={() => handlePause(trail.trail_id)}
                            disabled={busyId === trail.trail_id}
                            className="px-4 py-2 bg-surface-container-high text-on-surface font-bold rounded hover:opacity-90 disabled:opacity-50"
                          >
                            Pausar envios
                          </button>
                        )}
                        <button
                          onClick={() => handleUnsubscribe(trail.trail_id)}
                          disabled={busyId === trail.trail_id}
                          className="px-4 py-2 bg-error bg-opacity-10 text-error border border-error border-opacity-25 font-bold rounded hover:bg-opacity-20 transition-all disabled:opacity-50"
                        >
                          Cancelar inscrição
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleSubscribe(trail.trail_id)}
                        disabled={busyId === trail.trail_id}
                        className="px-4 py-2 bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50"
                      >
                        {isCompleted ? 'Refazer trilha' : 'Inscrever-se'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
